/**
 * Dashboard Component
 * 
 * SESマッチングシステムのメインダッシュボード画面
 * 主な機能:
 * - システム全体のメトリクス表示
 * - 最近の案件・エンジニア・活動の表示
 * - おすすめマッチングの表示
 * - 各管理画面への導線
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  alpha,
} from '@mui/material';
import {
  People,
  Work,
  AutoAwesome,
  TrendingUp,
  AttachMoney,
} from '@mui/icons-material';
import { dashboardService, DashboardMetrics, ProjectSummary, EngineerSummary } from '../services/dashboard';
import { SettingsService } from '../services/settings';
import { DashboardDisplaySettings } from '../types';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [engineers, setEngineers] = useState<EngineerSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [displaySettings, setDisplaySettings] = useState<DashboardDisplaySettings>({
    showMonthlyRevenue: true,
    showConversionRate: true,
    showMatchingAccuracy: true,
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // 必須データソース: これらがすべて失敗した場合はダッシュボード表示不可
      // 設定データは必須ではなく、デフォルト値にフォールバック可能
      const CRITICAL_DATA_SOURCES = ['メトリクス', '案件リスト', 'エンジニアリスト'];

      const [metricsResponse, projectsResponse, engineersResponse, settingsResponse] = await Promise.allSettled([
        dashboardService.getMetrics(),
        dashboardService.getRecentProjects(5),
        dashboardService.getAvailableEngineers(5),
        SettingsService.getSettings()
      ]);

      // クリティカルデータソースの失敗を記録
      const criticalFailures: string[] = [];

      if (metricsResponse.status === 'fulfilled' && !metricsResponse.value.error) {
        setMetrics(metricsResponse.value.data);
      } else {
        criticalFailures.push('メトリクス');
        console.error('❌ メトリクスの読み込みに失敗:', {
          errorId: 'DASHBOARD_METRICS_LOAD_FAILED',
          error: metricsResponse.status === 'fulfilled' ? metricsResponse.value.error : metricsResponse.reason,
          timestamp: new Date().toISOString()
        });
      }

      if (projectsResponse.status === 'fulfilled' && !projectsResponse.value.error) {
        setProjects(projectsResponse.value.data || []);
      } else {
        criticalFailures.push('案件リスト');
        console.error('❌ 案件リストの読み込みに失敗:', {
          errorId: 'DASHBOARD_PROJECTS_LOAD_FAILED',
          error: projectsResponse.status === 'fulfilled' ? projectsResponse.value.error : projectsResponse.reason,
          timestamp: new Date().toISOString()
        });
      }

      if (engineersResponse.status === 'fulfilled' && !engineersResponse.value.error) {
        setEngineers(engineersResponse.value.data || []);
      } else {
        criticalFailures.push('エンジニアリスト');
        console.error('❌ エンジニアリストの読み込みに失敗:', {
          errorId: 'DASHBOARD_ENGINEERS_LOAD_FAILED',
          error: engineersResponse.status === 'fulfilled' ? engineersResponse.value.error : engineersResponse.reason,
          timestamp: new Date().toISOString()
        });
      }

      // クリティカルデータソースがすべて失敗した場合はエラーをスロー
      // この判定は設定失敗を追加する前に行う必要がある
      if (criticalFailures.length === CRITICAL_DATA_SOURCES.length) {
        throw new Error('すべてのデータの読み込みに失敗しました。ネットワーク接続を確認してください。');
      }

      // 設定データの読み込み結果を処理
      // 設定データはクリティカルではないため、失敗してもデフォルト値で続行
      let settingsFailed = false;
      if (settingsResponse.status === 'fulfilled' && settingsResponse.value.data?.dashboardDisplay) {
        setDisplaySettings(settingsResponse.value.data.dashboardDisplay);
      } else {
        settingsFailed = true;
        const settingsError = settingsResponse.status === 'fulfilled'
          ? settingsResponse.value.error
          : settingsResponse.reason;
        console.error('❌ 設定の読み込みに失敗:', {
          errorId: 'DASHBOARD_SETTINGS_LOAD_FAILED',
          error: settingsError,
          timestamp: new Date().toISOString()
        });
      }

      // ユーザーへのエラーメッセージを構築
      const allFailures: string[] = [...criticalFailures];
      if (settingsFailed) {
        allFailures.push('設定（デフォルト値を使用）');
      }

      // 部分的失敗がある場合、具体的なエラーメッセージを表示
      if (allFailures.length > 0) {
        // 具体的なエラーメッセージを生成
        const errorDetails = allFailures.map(failure => {
          switch(failure) {
            case 'メトリクス': return 'システム統計の表示に問題があります';
            case '案件リスト': return '最新案件の表示に問題があります';
            case 'エンジニアリスト': return 'エンジニア情報の表示に問題があります';
            case '設定（デフォルト値を使用）': return 'カスタム設定の読み込みに失敗しました';
            default: return failure;
          }
        }).join('。');

        setError(`${errorDetails}。一部機能が制限されています。`);
      }

    } catch (err) {
      console.error('❌ ダッシュボードの読み込み処理でエラーが発生:', {
        errorId: 'DASHBOARD_LOAD_UNEXPECTED_ERROR',
        errorType: err instanceof Error ? err.constructor.name : typeof err,
        timestamp: new Date().toISOString()
      });

      const errorMessage = err instanceof Error ? err.message : '不明なエラー';
      setError(
        '予期しないエラーが発生しました。ページをリロードしてください。' +
        (process.env.NODE_ENV === 'development' ? ` (開発環境: ${errorMessage})` : '')
      );
    } finally {
      setLoading(false);
    }
  };

  function getStatsCards() {
    if (!metrics) return [];

    const baseCards = [
      { title: '登録エンジニア', value: `${metrics.totalEngineers}名`, icon: <People />, color: '#3498db' },
      { title: '稼働可能', value: `${metrics.availableEngineers}名`, icon: <People />, color: '#27ae60' },
      { title: 'アクティブ案件', value: `${metrics.activeProjects}件`, icon: <Work />, color: '#e74c3c' },
      { title: '新着案件', value: `${metrics.pendingProjects}件`, icon: <Work />, color: '#f39c12' },
    ];

    const conditionalCards = [];

    if (displaySettings.showConversionRate) {
      conditionalCards.push({ title: '成約率', value: `${metrics.conversionRate}%`, icon: <TrendingUp />, color: '#17a2b8' });
    }

    if (displaySettings.showMonthlyRevenue) {
      conditionalCards.push({ title: '月間売上', value: `${Math.round(metrics.monthlyRevenue / 10000)}万円`, icon: <AttachMoney />, color: '#2ecc71' });
    }

    if (displaySettings.showMatchingAccuracy) {
      conditionalCards.push({ title: 'マッチング精度', value: `${metrics.averageMatchScore}%`, icon: <AutoAwesome />, color: '#e67e22' });
    }

    return [...baseCards, ...conditionalCards];
  }

  function getPriorityColor(priority: string): string {
    switch (priority) {
      case 'high': return '#e74c3c';
      case 'medium': return '#f39c12';
      default: return '#95a5a6';
    }
  }

  function getStatusColor(status: string): string {
    switch (status) {
      case 'available': return '#27ae60';
      case 'busy': return '#e74c3c';
      default: return '#95a5a6';
    }
  }

  function formatTime(timestamp: string): string {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays}日前`;
    }
    if (diffHours > 0) {
      return `${diffHours}時間前`;
    }
    return '1時間前';
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadDashboardData}>
          再読み込み
        </Button>
      </Box>
    );
  }

  const statsCards = getStatsCards();

  return (
    <Box sx={{ animation: 'fadeIn 0.4s ease-out' }}>
      <Box sx={{ mb: 5 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 1,
            background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.02em',
          }}
        >
          営業管理ダッシュボード
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
          システム全体の状況と最新の活動を確認できます
        </Typography>
      </Box>

      {/* 統計カード */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 3,
        mb: 5,
      }}>
        {statsCards.map((card) => (
          <Card
            key={card.title}
            elevation={0}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 24px -10px rgba(0, 0, 0, 0.15)',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${card.color}, ${card.color}dd)`,
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    color="text.secondary"
                    gutterBottom
                    variant="body2"
                    sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: 1.5 }}
                  >
                    {card.title}
                  </Typography>
                  <Typography
                    variant="h4"
                    component="h2"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {card.value}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, ${card.color}15, ${card.color}05)`,
                    color: card.color,
                    fontSize: 24,
                  }}
                >
                  {card.icon}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* アサイン可能エンジニア */}
        <Card elevation={0}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '1.125rem',
                  color: 'text.primary',
                }}
              >
                アサイン可能エンジニア
              </Typography>
            </Box>
            {engineers.length > 0 ? engineers.map((engineer, index) => (
              <Box
                key={engineer.id}
                onClick={() => navigate(`/engineer/${engineer.id}`)}
                sx={{
                  borderBottom: index < engineers.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  py: 2.5,
                  px: 2,
                  mx: -2,
                  cursor: 'pointer',
                  borderRadius: '8px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.04),
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="h6"
                      component="h4"
                      sx={{
                        mb: 0.75,
                        fontWeight: 600,
                        fontSize: '1rem',
                        color: 'text.primary',
                      }}
                    >
                      {engineer.name}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      variant="body2"
                      sx={{
                        mb: 1.5,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      }}
                    >
                      経験年数: {engineer.experience_years || 0}年
                      {engineer.hourly_rate != null ? ` | 時給: ${engineer.hourly_rate.toLocaleString()}円` : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {engineer.skills.slice(0, 3).map((skill) => (
                        <Chip
                          key={skill}
                          label={skill}
                          size="small"
                          sx={{
                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                            color: (theme) => theme.palette.primary.main,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: '24px',
                          }}
                        />
                      ))}
                      {engineer.skills.length > 3 && (
                        <Chip
                          label={`+${engineer.skills.length - 3}`}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: '24px',
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Chip
                    label={engineer.availability_status === 'available' ? '稼働可能' : '稼働中'}
                    size="small"
                    sx={{
                      backgroundColor: getStatusColor(engineer.availability_status),
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: '28px',
                      ml: 2,
                    }}
                  />
                </Box>
              </Box>
            )) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography color="text.secondary" sx={{ fontWeight: 500 }}>
                  稼働可能なエンジニアがいません
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* 新着案件 */}
        <Card elevation={0}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '1.125rem',
                  color: 'text.primary',
                }}
              >
                新着案件
              </Typography>
            </Box>
            {projects.length > 0 ? projects.map((project, index) => (
              <Box
                key={project.id}
                onClick={() => navigate(`/project/${project.id}`)}
                sx={{
                  position: 'relative',
                  borderBottom: index < projects.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  py: 2.5,
                  px: 2,
                  mx: -2,
                  cursor: 'pointer',
                  borderRadius: '8px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.04),
                    transform: 'translateX(4px)',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '4px',
                    height: '60%',
                    backgroundColor: getPriorityColor(project.priority),
                    borderRadius: '0 2px 2px 0',
                  },
                  pl: 3,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="h6"
                      component="h4"
                      sx={{
                        mb: 0.75,
                        fontWeight: 600,
                        fontSize: '1rem',
                        color: 'text.primary',
                      }}
                    >
                      {project.title}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      variant="body2"
                      sx={{
                        mb: 1.5,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      }}
                    >
                      {project.client_name && `${project.client_name} | `}
                      {project.budget != null ? `予算: ${project.budget.toLocaleString()}万円` : ''}
                      {` | ステータス: ${project.status}`}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {project.urgent && (
                        <Chip
                          label="急募"
                          size="small"
                          sx={{
                            backgroundColor: '#FEE2E2',
                            color: '#DC2626',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            height: '24px',
                          }}
                        />
                      )}
                      {project.skills.slice(0, 3).map((skill) => (
                        <Chip
                          key={skill}
                          label={skill}
                          size="small"
                          sx={{
                            backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.08),
                            color: (theme) => theme.palette.secondary.dark,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: '24px',
                          }}
                        />
                      ))}
                      {project.skills.length > 3 && (
                        <Chip
                          label={`+${project.skills.length - 3}`}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: '24px',
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Typography
                    color="text.secondary"
                    variant="body2"
                    sx={{
                      ml: 2,
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      color: '#6B7280',
                    }}
                  >
                    {formatTime(project.created_at)}
                  </Typography>
                </Box>
              </Box>
            )) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography color="text.secondary" sx={{ fontWeight: 500 }}>
                  新着案件がありません
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Dashboard;